import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Users, Stethoscope, TrendingUp, CheckCircle2, Activity, FileText } from "lucide-react";

const COLORS = ["#00A85A", "#F59E0B", "#94A3B8"];

function Stat({ icon: Icon, label, value, suffix }) {
  return (
    <SurfaceCard className="p-5">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F7F0] dark:bg-[#00A85A]/15 mb-3"><Icon className="h-5 w-5 text-[#00A85A]" /></div>
      <div className="text-2xl font-extrabold text-[#101516] dark:text-white tabular-nums">{value}{suffix}</div>
      <div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-[#8B9698]">{label}</div>
    </SurfaceCard>
  );
}

const tooltipStyle = { backgroundColor: "#0E1A21", border: "1px solid #223139", borderRadius: 12, color: "#fff", fontSize: 12 };

export default function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="space-y-4"><Skeleton className="h-24 rounded-2xl" /><div className="grid grid-cols-2 gap-4"><Skeleton className="h-72 rounded-2xl" /><Skeleton className="h-72 rounded-2xl" /></div></div>;
  const s = d.summary;

  return (
    <div>
      <PageHeader title="Analytics" subtitle="Practice performance & clinical metrics" breadcrumb={["Management", "Analytics"]} />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <Stat icon={Users} label="Patient Volume" value={s.patient_volume} />
        <Stat icon={Stethoscope} label="Consultations" value={s.consultations} />
        <Stat icon={CheckCircle2} label="Treatment Accept." value={s.treatment_acceptance} suffix="%" />
        <Stat icon={Activity} label="Case Progression" value={s.case_progression} suffix="%" />
        <Stat icon={TrendingUp} label="Follow-up Compl." value={s.followup_completion} suffix="%" />
        <Stat icon={FileText} label="Reports" value={s.reports_generated} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SurfaceCard className="p-5">
          <h3 className="text-sm font-bold text-[#101516] dark:text-white mb-4">Patient Volume Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={d.patient_volume_trend}>
              <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00A85A" stopOpacity={0.3} /><stop offset="100%" stopColor="#00A85A" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8B9698" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8B9698" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="patients" stroke="#00A85A" strokeWidth={2.5} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </SurfaceCard>

        <SurfaceCard className="p-5">
          <h3 className="text-sm font-bold text-[#101516] dark:text-white mb-4">Consultations vs Accepted</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.consultations_trend} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8B9698" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8B9698" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="consultations" fill="#CBD5E1" radius={[6, 6, 0, 0]} barSize={14} />
              <Bar dataKey="accepted" fill="#00A85A" radius={[6, 6, 0, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </SurfaceCard>

        <SurfaceCard className="p-5">
          <h3 className="text-sm font-bold text-[#101516] dark:text-white mb-4">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={d.revenue_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8B9698" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8B9698" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => `$${v.toLocaleString()}`} />
              <Line type="monotone" dataKey="revenue" stroke="#00A85A" strokeWidth={2.5} dot={{ fill: "#00A85A", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </SurfaceCard>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <SurfaceCard className="p-5">
            <h3 className="text-sm font-bold text-[#101516] dark:text-white mb-4">Report Status</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={d.report_status} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {d.report_status.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap justify-center gap-3 mt-2">{d.report_status.map((e, i) => <div key={i} className="flex items-center gap-1.5 text-xs text-[#687173] dark:text-[#8B9698]"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{e.name}</div>)}</div>
          </SurfaceCard>

          <SurfaceCard className="p-5">
            <h3 className="text-sm font-bold text-[#101516] dark:text-white mb-4">Cases by Stage</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={d.case_stages} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" hide /><YAxis type="category" dataKey="stage" width={90} tick={{ fontSize: 10, fill: "#8B9698" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#00A85A" radius={[0, 6, 6, 0]} barSize={12} />
              </BarChart>
            </ResponsiveContainer>
          </SurfaceCard>
        </div>
      </div>
    </div>
  );
}
